import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getUserAccountDetailsAPI } from "api/account";
import { decodeObject, encodeObject } from "shared/utils/encryption";
import { tokenManager } from "services/client/token-manager";

const DEFAULT_ACCOUNT_DETAILS_EXPIRES_DAYS = 7;
/** Hard cap: never block the shell longer than this when cache is empty. */
const INITIAL_ACCOUNT_FETCH_FALLBACK_MS = 5000;
const ACCOUNT_FETCH_MAX_ATTEMPTS = 3;
const ACCOUNT_FETCH_RETRY_BASE_MS = 400;
/** Background refresh if cache is older than this when tab becomes visible. */
const ACCOUNT_DETAILS_STALE_MS = 5 * 60 * 1000;
/** Avoid hammering the API when visibility/online fire in quick succession. */
const MIN_MS_BETWEEN_SILENT_REFRESHES = 30 * 1000;

const debugLog = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[AccountDetails]", ...args);
  }
};

function isBrowserOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

// --- Navigation (permissive: never skip fetch when uncertain) -----------------

function getDocumentNavigationType() {
  try {
    const entries = performance.getEntriesByType?.("navigation");
    const entry = entries?.[0];
    if (entry && typeof entry.type === "string" && entry.type !== "") {
      return entry.type;
    }
  } catch {
    // Restricted / unsupported
  }

  try {
    const nav = performance.navigation;
    if (nav && typeof nav.type === "number") {
      if (nav.type === 0) return "navigate";
      if (nav.type === 1) return "reload";
      if (nav.type === 2) return "back_forward";
    }
  } catch {
    // Legacy API missing
  }

  return null;
}

function shouldAutoFetchOnThisDocumentLoad() {
  const type = getDocumentNavigationType();
  if (!type) {
    return true;
  }
  return (
    type === "navigate" || type === "reload" || type === "back_forward" || type === "prerender"
  );
}

export const AccountDetailsContext = createContext(null);

// --- Role & payload normalization ---------------------------------------------

function dedupeRolesPreserveVendorFirst(roleList) {
  if (!Array.isArray(roleList)) {
    return roleList;
  }
  const seen = new Set();
  const unique = [];
  for (const r of roleList) {
    if (r == null || r === "") continue;
    if (!seen.has(r)) {
      seen.add(r);
      unique.push(r);
    }
  }
  const hasVendor = unique.includes("vendor");
  if (!hasVendor) {
    return unique;
  }
  return ["vendor", ...unique.filter((r) => r !== "vendor")];
}

function normalizeAccountPayload(raw) {
  const accountDetails = { ...raw };
  if (accountDetails.socket_id) {
    delete accountDetails.socket_id;
  }
  if (Array.isArray(accountDetails.role)) {
    accountDetails.role = dedupeRolesPreserveVendorFirst(accountDetails.role);
  }
  return accountDetails;
}

// --- API response parsing -----------------------------------------------------

function extractHttpStatus(accountResult) {
  if (accountResult == null || typeof accountResult !== "object") {
    return undefined;
  }
  const s =
    accountResult.statusCode ??
    accountResult.status ??
    accountResult?.data?.statusCode ??
    accountResult?.data?.status;
  return typeof s === "number" ? s : undefined;
}

function extractRawAccountPayload(accountResult) {
  if (accountResult == null || typeof accountResult !== "object") {
    return null;
  }
  let raw =
    accountResult.results ?? accountResult?.data?.results ?? accountResult.data ?? accountResult;

  if (
    raw &&
    typeof raw === "object" &&
    raw.data &&
    typeof raw.data === "object" &&
    !raw.id &&
    !raw.role
  ) {
    raw = raw.data;
  }

  if (
    raw &&
    typeof raw === "object" &&
    raw.data &&
    typeof raw.data === "object" &&
    (raw.data.id || raw.data.role)
  ) {
    raw = raw.data;
  }

  return raw;
}

function isPlausibleAccountObject(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return false;
  }
  const hasId = raw.id != null && raw.id !== "";
  const hasRoleList = Array.isArray(raw.role) && raw.role.length > 0;
  return hasId || hasRoleList;
}

function parseAccountApiResponse(accountResult) {
  if (accountResult == null || accountResult === 0) {
    return { ok: false, message: "Empty account response." };
  }

  const status = extractHttpStatus(accountResult);
  const raw = extractRawAccountPayload(accountResult);
  const msg = accountResult?.message || accountResult?.data?.message || "Invalid account response.";

  if (status != null) {
    if (status >= 400) {
      return { ok: false, status, message: msg };
    }
    if (status < 200 || status >= 300) {
      return {
        ok: false,
        status,
        message: msg !== "Invalid account response." ? msg : `Unexpected HTTP status ${status}.`,
      };
    }
  }

  if (isPlausibleAccountObject(raw)) {
    return { ok: true, raw };
  }

  return {
    ok: false,
    status,
    message:
      status != null && status >= 200 && status < 300
        ? accountResult?.message ||
          accountResult?.data?.message ||
          "Account payload missing required fields."
        : msg,
  };
}

// --- Salt & stored cookie validation -----------------------------------------

function getLoginSaltStrict() {
  const salt = process.env.REACT_APP_LOGIN_SALT;
  if (salt) {
    return salt;
  }
  if (process.env.NODE_ENV === "development") {
    throw new Error(
      "[AccountDetailsProvider] REACT_APP_LOGIN_SALT is not configured. Set it in your environment."
    );
  }
  // eslint-disable-next-line no-console
  console.error(
    "[AccountDetailsProvider] REACT_APP_LOGIN_SALT is not configured; account details cannot be encrypted or validated."
  );
  return null;
}

function tryParseStoredAccountDetails(encryptedCookie) {
  const salt = process.env.REACT_APP_LOGIN_SALT;
  if (!encryptedCookie || !salt) {
    return { ok: false, reason: "missing" };
  }
  try {
    const obj = decodeObject(encryptedCookie, salt);
    if (!obj || typeof obj !== "object") {
      return { ok: false, reason: "invalid_shape" };
    }
    if (!isPlausibleAccountObject(obj)) {
      return { ok: false, reason: "incomplete" };
    }
    return { ok: true, data: obj };
  } catch (err) {
    debugLog("Stored accountDetails cookie could not be decoded (corrupt or wrong salt).", err);
    return { ok: false, reason: "decode_error", error: err };
  }
}

function scrubInvalidAccountCookie() {
  const encrypted = tokenManager.getAccountDetails();
  const parsed = tryParseStoredAccountDetails(encrypted);
  if (!parsed.ok) {
    if (encrypted) {
      tokenManager.clearAccountDetails();
      // eslint-disable-next-line no-console
      console.warn("[AccountDetailsProvider] Removed invalid accountDetails cookie.");
    }
    return false;
  }
  return true;
}

function hasTrustworthyCachedAccountDetails() {
  return scrubInvalidAccountCookie();
}

function shouldDeferChildrenForInitialAccountFetch() {
  if (!tokenManager.getAccess() && !tokenManager.getAuth()) {
    return false;
  }
  if (!shouldAutoFetchOnThisDocumentLoad()) {
    return false;
  }
  if (hasTrustworthyCachedAccountDetails()) {
    return false;
  }
  return true;
}

function isAccountCacheStale(lastSuccessAtMs) {
  if (!lastSuccessAtMs || lastSuccessAtMs <= 0) {
    return true;
  }
  return Date.now() - lastSuccessAtMs > ACCOUNT_DETAILS_STALE_MS;
}

// --- Retry & auth failure handling --------------------------------------------

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shouldRetryParsedResponse(parsed) {
  if (!parsed || parsed.ok) return false;
  const s = parsed.status;
  if (s === 401 || s === 403) return false;
  return s != null && s >= 500;
}

function clearSessionAfterAuthFailure() {
  debugLog("Clearing session tokens after account fetch auth failure.");
  tokenManager.clearAll();
}

/**
 * @param {{ status?: number, parsed?: object, err?: Error }} ctx
 */
function applySessionClearAfterAuthFailure(clearSessionOnAuthFailure, onAuthFailure, ctx) {
  if (onAuthFailure) {
    try {
      if (onAuthFailure(ctx) === false) {
        return;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[AccountDetails] onAuthFailure error:", e);
    }
  }
  if (clearSessionOnAuthFailure === false) {
    return;
  }
  clearSessionAfterAuthFailure();
}

function handleNonAuthClientFailure(parsed, err) {
  const status = parsed?.status ?? err?.status ?? err?.response?.status;
  if (status != null && status >= 400 && status < 500 && status !== 401 && status !== 403) {
    tokenManager.clearAccountDetails();
  }
}

// --- Provider ----------------------------------------------------------------

/**
 * @param {{
 *   children: React.ReactNode,
 *   onAuthFailure?: (info: { status?: number, parsed?: object, err?: Error, phase: 'afterRetry' | 'immediate' }) => boolean | void,
 *   clearSessionOnAuthFailure?: false | 'immediate' | 'retryOnce',
 * }} props
 */
export function AccountDetailsProvider({
  children,
  onAuthFailure,
  clearSessionOnAuthFailure = "retryOnce",
}) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [childrenReady, setChildrenReady] = useState(
    () => !shouldDeferChildrenForInitialAccountFetch()
  );

  const mountedRef = useRef(true);
  const refreshInFlightRef = useRef(null);
  /** @type {React.MutableRefObject<Array<() => void> | null>} */
  const completionQueueForActiveRunRef = useRef(null);
  const lastSuccessfulFetchAtRef = useRef(
    typeof sessionStorage !== "undefined"
      ? Number(sessionStorage.getItem("accountDetailsLastOkAt")) || 0
      : 0
  );
  const lastSilentRefreshStartedAtRef = useRef(0);
  const swrOnceRef = useRef(false);
  const authFailurePropsRef = useRef({ onAuthFailure, clearSessionOnAuthFailure });
  authFailurePropsRef.current = { onAuthFailure, clearSessionOnAuthFailure };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetChildrenReady = useCallback((next) => {
    setChildrenReady((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      if (prev === value) {
        return prev;
      }
      return value;
    });
  }, []);

  const safeSetIsRefreshing = useCallback((value) => {
    if (!mountedRef.current) return;
    setIsRefreshing(value);
  }, []);

  const safeSetLastError = useCallback((value) => {
    if (!mountedRef.current) return;
    setLastError(value);
  }, []);

  const persistLastOkTimestamp = useCallback(() => {
    const t = Date.now();
    lastSuccessfulFetchAtRef.current = t;
    try {
      sessionStorage.setItem("accountDetailsLastOkAt", String(t));
    } catch {
      // private mode / quota
    }
  }, []);

  const flushCompletionQueue = useCallback((queue) => {
    if (!queue || queue.length === 0) return;
    for (let i = 0; i < queue.length; i++) {
      const fn = queue[i];
      try {
        fn();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[AccountDetails] onComplete callback error:", e);
      }
    }
  }, []);

  const runAccountFetchWithRetries = useCallback(async ({ allowRetries = true } = {}) => {
    let lastResult;
    let authUnauthorizedRetryDone = false;

    for (let attempt = 1; attempt <= ACCOUNT_FETCH_MAX_ATTEMPTS; attempt++) {
      if (isBrowserOffline()) {
        const err = Object.assign(new Error("Network offline"), { code: "OFFLINE" });
        // eslint-disable-next-line no-console
        console.error("[AccountDetails] Skipping account fetch — offline.");
        throw err;
      }

      try {
        lastResult = await getUserAccountDetailsAPI();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[AccountDetails] getUserAccountDetailsAPI error:", err);
        const status = err?.status ?? err?.response?.status;
        const mode = authFailurePropsRef.current.clearSessionOnAuthFailure;
        if (status === 401 || status === 403) {
          if (mode === "retryOnce" && !authUnauthorizedRetryDone) {
            authUnauthorizedRetryDone = true;
            debugLog("401/403 from transport — retrying once before surfacing.");
            await delay(250);
            continue;
          }
          throw err;
        }
        if (!allowRetries || attempt >= ACCOUNT_FETCH_MAX_ATTEMPTS || isBrowserOffline()) {
          throw err;
        }
        debugLog(`Account fetch attempt ${attempt} threw, retrying…`, err?.message);
        await delay(ACCOUNT_FETCH_RETRY_BASE_MS * attempt);
        continue;
      }

      const parsed = parseAccountApiResponse(lastResult);
      if (parsed.ok) {
        return { result: lastResult, parsed };
      }

      if (parsed.status === 401 || parsed.status === 403) {
        const mode = authFailurePropsRef.current.clearSessionOnAuthFailure;
        if (mode === "retryOnce" && !authUnauthorizedRetryDone) {
          authUnauthorizedRetryDone = true;
          debugLog("401/403 from API — retrying once before session handling.");
          await delay(250);
          continue;
        }
        return { result: lastResult, parsed };
      }

      if (
        shouldRetryParsedResponse(parsed) &&
        attempt < ACCOUNT_FETCH_MAX_ATTEMPTS &&
        allowRetries
      ) {
        debugLog(
          `Account fetch attempt ${attempt} failed (${parsed.status}), retrying…`,
          parsed.message
        );
        await delay(ACCOUNT_FETCH_RETRY_BASE_MS * attempt);
        continue;
      }
      return { result: lastResult, parsed };
    }
    return { result: lastResult, parsed: parseAccountApiResponse(lastResult) };
  }, []);

  const refreshAccountDetails = useCallback(
    (options = {}) => {
      const { expires: expiresOpt, refreshExpiry, silent = true, onComplete } = options;

      if (refreshInFlightRef.current) {
        const q = completionQueueForActiveRunRef.current;
        if (q && onComplete) {
          q.push(onComplete);
        }
        return refreshInFlightRef.current;
      }

      const expires = expiresOpt ?? refreshExpiry ?? DEFAULT_ACCOUNT_DETAILS_EXPIRES_DAYS;

      const completionQueue = [];
      if (onComplete) {
        completionQueue.push(onComplete);
      }
      completionQueueForActiveRunRef.current = completionQueue;

      const promise = (async () => {
        if (!silent) {
          safeSetIsRefreshing(true);
        }
        safeSetLastError(null);

        const finish = () => {
          if (!silent) {
            safeSetIsRefreshing(false);
          }
          const q = completionQueueForActiveRunRef.current;
          completionQueueForActiveRunRef.current = null;
          flushCompletionQueue(q);
        };

        try {
          let salt;
          try {
            salt = getLoginSaltStrict();
          } catch (saltErr) {
            // eslint-disable-next-line no-console
            console.error("[AccountDetails]", saltErr);
            safeSetLastError(saltErr);
            return { ok: false, error: saltErr, message: saltErr.message };
          }

          if (!salt) {
            const err = new Error("REACT_APP_LOGIN_SALT is not configured");
            safeSetLastError(err);
            return { ok: false, error: err, message: err.message };
          }

          let parsed;
          try {
            const { parsed: p } = await runAccountFetchWithRetries({ allowRetries: true });
            parsed = p;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[AccountDetails] Account fetch failed after retries:", err);
            if (!silent) {
              // eslint-disable-next-line no-console
              console.error(
                "[AccountDetails] silent:false refresh failed:",
                err?.message || err?.data?.message
              );
            }
            safeSetLastError(err);
            const message =
              err?.code === "OFFLINE"
                ? "You appear to be offline."
                : err?.message || err?.data?.message || "Could not refresh account details.";
            const status = err?.status ?? err?.response?.status;
            if (status === 401 || status === 403) {
              const { clearSessionOnAuthFailure: mode, onAuthFailure: onAuth } =
                authFailurePropsRef.current;
              applySessionClearAfterAuthFailure(mode, onAuth, { status, err });
            } else if (err?.code !== "OFFLINE") {
              handleNonAuthClientFailure(null, err);
            }
            return { ok: false, error: err, message };
          }

          if (!parsed.ok) {
            const msg = parsed.message || "Could not fetch account details.";
            const err = new Error(msg);
            // eslint-disable-next-line no-console
            console.error("[AccountDetails] Account response not OK:", msg, {
              status: parsed.status,
            });
            safeSetLastError(err);

            const status = parsed.status;
            if (status === 401 || status === 403) {
              const { clearSessionOnAuthFailure: mode, onAuthFailure: onAuth } =
                authFailurePropsRef.current;
              applySessionClearAfterAuthFailure(mode, onAuth, { status, parsed });
            } else {
              handleNonAuthClientFailure(parsed, null);
            }
            return { ok: false, error: err, message: msg, status: parsed.status };
          }

          const accountDetails = normalizeAccountPayload(parsed.raw);
          const encrypted = encodeObject(accountDetails, salt);
          tokenManager.setAccountDetails(encrypted, expires);
          persistLastOkTimestamp();

          return { ok: true, accountDetails };
        } finally {
          finish();
        }
      })();

      refreshInFlightRef.current = promise;
      promise.finally(() => {
        if (refreshInFlightRef.current === promise) {
          refreshInFlightRef.current = null;
        }
      });

      return promise;
    },
    [
      flushCompletionQueue,
      persistLastOkTimestamp,
      runAccountFetchWithRetries,
      safeSetIsRefreshing,
      safeSetLastError,
    ]
  );

  const maybeSilentRefresh = useCallback(
    (reason) => {
      if (!mountedRef.current) return;
      if (!tokenManager.getAccess() && !tokenManager.getAuth()) return;
      const now = Date.now();
      if (now - lastSilentRefreshStartedAtRef.current < MIN_MS_BETWEEN_SILENT_REFRESHES) {
        return;
      }
      if (isBrowserOffline()) {
        debugLog(`Silent refresh skipped (${reason}) — offline.`);
        return;
      }
      lastSilentRefreshStartedAtRef.current = now;
      void refreshAccountDetails({ silent: true });
    },
    [refreshAccountDetails]
  );

  /** Never leave the shell blank indefinitely when blocking for first load. */
  useEffect(() => {
    if (childrenReady) {
      return undefined;
    }
    const id = window.setTimeout(() => {
      if (mountedRef.current) {
        safeSetChildrenReady(true);
      }
    }, INITIAL_ACCOUNT_FETCH_FALLBACK_MS);
    return () => window.clearTimeout(id);
  }, [childrenReady, safeSetChildrenReady]);

  /** Initial fetch only when there is no valid cache (blocking path). */
  useEffect(() => {
    if (!shouldDeferChildrenForInitialAccountFetch()) {
      safeSetChildrenReady(true);
      return undefined;
    }

    void refreshAccountDetails({
      silent: true,
      onComplete: () => {
        if (mountedRef.current) {
          safeSetChildrenReady(true);
        }
      },
    });

    return undefined;
  }, [refreshAccountDetails, safeSetChildrenReady]);

  /** Stale-while-revalidate: valid cache → one background refresh after mount. */
  useEffect(() => {
    if (swrOnceRef.current) return undefined;
    if (!tokenManager.getAccess() && !tokenManager.getAuth()) return undefined;
    if (!hasTrustworthyCachedAccountDetails()) return undefined;
    swrOnceRef.current = true;
    maybeSilentRefresh("swr-mount");
    return undefined;
  }, [maybeSilentRefresh]);

  /** When tab visible — refresh if missing or stale. */
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "visible" || !mountedRef.current) return;
      if (!tokenManager.getAccess() && !tokenManager.getAuth()) return;
      if (!hasTrustworthyCachedAccountDetails()) {
        maybeSilentRefresh("visibility-missing");
        return;
      }
      if (isAccountCacheStale(lastSuccessfulFetchAtRef.current)) {
        maybeSilentRefresh("visibility-stale");
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [maybeSilentRefresh]);

  /** When network returns, catch up if we were missing data or cache is stale. */
  useEffect(() => {
    const onOnline = () => {
      if (!mountedRef.current) return;
      if (!tokenManager.getAccess() && !tokenManager.getAuth()) return;
      if (
        !hasTrustworthyCachedAccountDetails() ||
        isAccountCacheStale(lastSuccessfulFetchAtRef.current)
      ) {
        maybeSilentRefresh("online");
      }
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [maybeSilentRefresh]);

  const value = useMemo(
    () => ({
      isRefreshing,
      lastError,
      refreshAccountDetails,
    }),
    [isRefreshing, lastError, refreshAccountDetails]
  );

  return (
    <AccountDetailsContext.Provider value={value}>
      {childrenReady ? children : null}
    </AccountDetailsContext.Provider>
  );
}

export function useAccountDetails() {
  const ctx = useContext(AccountDetailsContext);
  if (!ctx) {
    throw new Error("useAccountDetails must be used within AccountDetailsProvider");
  }
  return ctx;
}
